import React, { useState, useEffect } from 'react';
import { clickable } from '../../../utils/a11y';
import { Link, useNavigate } from 'react-router-dom';
import './CloudStaffDashboard.css';
import cloudsafeLogo from '../../../assets/cloudsafe-logo.png';
import CloudStaffSidebar from './CloudStaffSidebar';
import {
  FaArrowLeft, FaShieldAlt, FaServer, FaCheckCircle, FaTimesCircle,
  FaExclamationTriangle, FaUserCircle, FaSignOutAlt, FaBell, FaSyncAlt, FaBars
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import { getUserRole } from '../../../utils/roleUtils';
import { clearToken } from '../../../utils/auth';
import { getApiEndpoint } from '../../../config/api';

const DataIntegrity = () => {
  const navigate = useNavigate();
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [userName, setUserName] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const userRole = getUserRole();
    if (userRole !== 'cloud_staff' && userRole !== 'super_admin') { toast.error('Access denied'); navigate('/login'); return; }
    loadProfile(); loadData();
  }, [navigate]);

  const loadProfile = async () => {
    try {
      const res = await fetch(getApiEndpoint('/api/users/profile'), { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } });
      if (res.ok) { const d = await res.json(); setUserName(`${d.firstName || d.first_name || ''} ${d.lastName || d.last_name || ''}`); }
    } catch (e) {}
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await fetch(getApiEndpoint('/api/cloud-staff/data-integrity'), { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } });
      if (res.ok) setData(await res.json());
      else toast.error('Failed to load data integrity checks');
    } catch (e) { toast.error('Failed to run integrity checks'); }
    finally { setLoading(false); }
  };

  const handleLogout = () => { clearToken(); localStorage.removeItem('userRole'); localStorage.removeItem('token'); navigate('/login'); };

  const severityIcon = (severity) => {
    if (severity === 'critical') return <FaTimesCircle style={{ color: '#f85149' }} />;
    if (severity === 'warning') return <FaExclamationTriangle style={{ color: '#ffa657' }} />;
    return <FaCheckCircle style={{ color: '#3fb950' }} />;
  };

  const severityColor = (severity) => {
    if (severity === 'critical') return '#f85149';
    if (severity === 'warning') return '#ffa657';
    return '#3fb950';
  };

  return (
    <div className="cs-dashboard">
      <header className="cs-header">
        <div className="cs-header-left">
          <button className="cs-mobile-menu-btn" onClick={() => setMobileSidebarOpen(true)}><FaBars /></button>
          <img src={cloudsafeLogo} alt="CloudSafe" className="cs-logo" />
          <div className="cs-header-badge"><FaServer /><span>Cloud Staff Portal</span></div>
        </div>
        <div className="cs-header-right">
          <div className="cs-header-icons">
            <button className="cs-icon-btn"><FaBell /></button>
            <div className="cs-user-avatar" aria-haspopup="menu" aria-expanded={showUserMenu}
                 {...clickable(() => setShowUserMenu(!showUserMenu), { label: 'User menu' })}>
              <FaUserCircle />
              {showUserMenu && (
                <div className="cs-user-menu">
                  <div className="cs-user-menu-header"><FaUserCircle className="cs-menu-avatar" /><div><p className="cs-menu-name">{userName}</p><p className="cs-menu-role">Cloud Staff Admin</p></div></div>
                  <div className="cs-user-menu-divider" />
                  <button onClick={handleLogout} className="cs-menu-item cs-menu-logout"><FaSignOutAlt /> Sign Out</button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>
      <div className="cs-layout">
        <CloudStaffSidebar active="data-integrity" mobileOpen={mobileSidebarOpen} onClose={() => setMobileSidebarOpen(false)} />
        <main className="cs-content">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem', paddingBottom: '1rem', borderBottom: '1px solid #21262d' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <Link to="/cloud-staff/dashboard" className="act-back-btn"><FaArrowLeft /></Link>
              <div>
                <h1 style={{ fontSize: '1.4rem', fontWeight: 700, color: '#f0f6fc', margin: 0 }}><FaShieldAlt style={{ marginRight: '0.5rem', color: '#ffa657' }} /> Data Integrity</h1>
                <p style={{ fontSize: '0.8rem', color: '#8b949e', margin: '0.2rem 0 0' }}>Automated data quality validation across all assets</p>
              </div>
            </div>
            <button onClick={loadData} className="cs-primary-btn" disabled={loading}><FaSyncAlt /> Re-scan</button>
          </div>

          {loading ? (
            <div className="cs-loading"><div className="cs-spinner" /><p>Running integrity checks...</p></div>
          ) : data ? (
            <>
              {/* Summary */}
              <div className="cs-stats-grid" style={{ marginBottom: '2rem' }}>
                <div className="cs-stat-card">
                  <div className="cs-stat-info" style={{ width: '100%', textAlign: 'center' }}>
                    <span className="cs-stat-value">{data.summary?.total_checks || 0}</span>
                    <span className="cs-stat-label">Total Issues Found</span>
                  </div>
                </div>
                <div className="cs-stat-card">
                  <div className="cs-stat-info" style={{ width: '100%', textAlign: 'center' }}>
                    <span className="cs-stat-value" style={{ color: '#f85149' }}>{data.summary?.critical || 0}</span>
                    <span className="cs-stat-label">Critical</span>
                  </div>
                </div>
                <div className="cs-stat-card">
                  <div className="cs-stat-info" style={{ width: '100%', textAlign: 'center' }}>
                    <span className="cs-stat-value" style={{ color: '#ffa657' }}>{data.summary?.warnings || 0}</span>
                    <span className="cs-stat-label">Warnings</span>
                  </div>
                </div>
                <div className="cs-stat-card">
                  <div className="cs-stat-info" style={{ width: '100%', textAlign: 'center' }}>
                    <span className="cs-stat-value" style={{ color: data.checks?.length === 0 ? '#3fb950' : '#8b949e' }}>
                      {data.checks?.length === 0 ? 'All Clear' : 'Issues Found'}
                    </span>
                    <span className="cs-stat-label">Status</span>
                  </div>
                </div>
              </div>

              {/* Checks List */}
              {data.checks && data.checks.length > 0 ? (
                <div className="cs-section">
                  <h2 className="cs-section-title">All Issues</h2>
                  <div className="cs-activity-table">
                    <table>
                      <thead>
                        <tr><th>Severity</th><th>Check</th><th>Asset Type</th><th>Count</th><th>Description</th></tr>
                      </thead>
                      <tbody>
                        {data.checks.map((check, i) => (
                          <tr key={i}>
                            <td>
                              <span style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', color: severityColor(check.severity) }}>
                                {severityIcon(check.severity)}
                                <span style={{ textTransform: 'capitalize', fontWeight: 600, fontSize: '0.75rem' }}>{check.severity}</span>
                              </span>
                            </td>
                            <td><span className="cs-badge module">{check.check}</span></td>
                            <td>{check.label || check.asset_type || '—'}</td>
                            <td style={{ fontWeight: 600 }}>{check.count}</td>
                            <td>{check.message}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <div className="cs-empty-state">
                  <FaCheckCircle style={{ color: '#3fb950', fontSize: '3rem' }} />
                  <p style={{ color: '#3fb950', fontWeight: 600 }}>All integrity checks passed! No issues found.</p>
                </div>
              )}
            </>
          ) : (
            <div className="cs-empty-state"><FaTimesCircle /><p>Could not load integrity data</p></div>
          )}
        </main>
      </div>
    </div>
  );
};

export default DataIntegrity;
